/**
 * Per-user terminal preferences (add-terminal-prefs-unlink-agent).
 * Stored in localStorage like the summary sort; sent as `terminal`
 * overrides in POST /sessions and applied to the SSH guacd handshake.
 */

export type TerminalSchemeId =
  | "default"
  | "black-white"
  | "white-black"
  | "gray-black"
  | "green-black"
  | "custom";

export type TerminalPrefs = {
  fontName: string;
  fontSize: number;
  scheme: TerminalSchemeId;
  customFg: string;
  customBg: string;
};

// Curated to the fonts actually installed in the guacd image
// (fonts-dejavu-core): unknown families fall back silently via fontconfig
// and render inconsistently, so only offer what exists server-side.
export const TERMINAL_FONTS = [
  "monospace",
  "DejaVu Sans Mono",
  "DejaVu Sans",
] as const;

export const TERMINAL_SCHEMES: TerminalSchemeId[] = [
  "default",
  "black-white",
  "white-black",
  "gray-black",
  "green-black",
  "custom",
];

export const TERMINAL_DEFAULTS: TerminalPrefs = {
  fontName: "monospace",
  fontSize: 12,
  scheme: "default",
  customFg: "#ffffff",
  customBg: "#000000",
};

const KEY = "visera:terminal-prefs:v1";

function clampSize(n: unknown): number {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.round(n) : NaN;
  if (Number.isNaN(v)) return TERMINAL_DEFAULTS.fontSize;
  return Math.min(72, Math.max(6, v));
}

function cleanColor(v: unknown, fallback: string): string {
  return typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v)
    ? v.toLowerCase()
    : fallback;
}

export function loadTerminalPrefs(): TerminalPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...TERMINAL_DEFAULTS };
    const data = JSON.parse(raw) as Partial<TerminalPrefs>;
    return {
      fontName:
        typeof data.fontName === "string" && data.fontName.length > 0
          ? data.fontName.slice(0, 64)
          : TERMINAL_DEFAULTS.fontName,
      fontSize: clampSize(data.fontSize),
      scheme: TERMINAL_SCHEMES.includes(data.scheme as TerminalSchemeId)
        ? (data.scheme as TerminalSchemeId)
        : TERMINAL_DEFAULTS.scheme,
      customFg: cleanColor(data.customFg, TERMINAL_DEFAULTS.customFg),
      customBg: cleanColor(data.customBg, TERMINAL_DEFAULTS.customBg),
    };
  } catch {
    return { ...TERMINAL_DEFAULTS };
  }
}

export function saveTerminalPrefs(prefs: TerminalPrefs): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        fontName: prefs.fontName.slice(0, 64),
        fontSize: clampSize(prefs.fontSize),
        scheme: prefs.scheme,
        customFg: cleanColor(prefs.customFg, TERMINAL_DEFAULTS.customFg),
        customBg: cleanColor(prefs.customBg, TERMINAL_DEFAULTS.customBg),
      }),
    );
  } catch {
    /* ignore */
  }
}

function hexToGuacd(hex: string): string {
  const r = hex.slice(1, 3);
  const g = hex.slice(3, 5);
  const b = hex.slice(5, 7);
  return `rgb:${r}/${g}/${b}`;
}

export type SessionTerminal = {
  font_name: string;
  font_size: number;
  color_scheme: string;
};

/** Map user prefs to the POST /sessions `terminal` body (guacd syntax). */
export function toSessionTerminal(prefs: TerminalPrefs): SessionTerminal {
  const color_scheme =
    prefs.scheme === "default"
      ? ""
      : prefs.scheme === "custom"
        ? `foreground: ${hexToGuacd(prefs.customFg)};background: ${hexToGuacd(prefs.customBg)};`
        : prefs.scheme;
  return {
    font_name: prefs.fontName,
    font_size: clampSize(prefs.fontSize),
    color_scheme,
  };
}
